import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  TablePagination,
  List
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Warning as WarningIcon,
  School as SchoolIcon,
  Payments as PaymentsIcon
} from '@mui/icons-material';
import { esES } from '@mui/x-data-grid/locales';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { parentService } from '../../services/api';
import { exportToExcel } from '../../utils/excelExport';

interface Parent {
  id: number;
  russian_name: string;
  spanish_name: string | null;
  email: string | null;
  phone: string | null;
  country_code: string;
  is_active: boolean;
  notes: string | null;
  students_count?: number;
  total_paid?: number;
  created_at: string;
}

const ParentList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [parents, setParents] = useState<Parent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  
  // Filtros
  const [filters, setFilters] = useState({
    search: '',
    active: '',
    has_students: ''
  });
  
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedParent, setSelectedParent] = useState<Parent | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [parentDetails, setParentDetails] = useState<any>(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Estados para padres pendientes
  const [pendingParents, setPendingParents] = useState<any[]>([]);
  const [loadingPending, setLoadingPending] = useState(false);

  useEffect(() => {
    loadParents();
    loadPendingParents();
  }, [paginationModel, filters]);

  const loadParents = async () => {
    try {
      setLoading(true);
      
      const params: any = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        ...filters
      };
      
      // Limpiar filtros vacíos
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await parentService.getAll(params);
      
      setParents(response.data.data);
      setTotal(response.data.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar padres');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingParents = async () => {
    try {
      setLoadingPending(true);
      const response = await parentService.getPendingApproval();
      setPendingParents(response.data.data || []);
    } catch (err) {
      console.error('Error loading pending parents:', err);
    } finally {
      setLoadingPending(false);
    }
  };

  const loadParentDetails = async (id: number) => {
    try {
      const response = await parentService.getById(id);
      setParentDetails(response.data);
      setDetailsDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar detalles');
    }
  };

  const handleDelete = async () => {
    if (!selectedParent) return;
    
    try {
      await parentService.delete(selectedParent.id);
      loadParents();
      setDeleteDialogOpen(false);
      setSelectedParent(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar padre');
    }
  };

  const handleApprove = async (userId: number) => {
    try {
      await parentService.approveParent(userId);
      loadPendingParents();
      loadParents();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al aprobar padre');
    }
  };

  const handleExport = () => {
    const data = parents.map(parent => ({
      'ID': parent.id,
      'Nombre Ruso': parent.russian_name,
      'Nombre Español': parent.spanish_name || '',
      'Email': parent.email || '',
      'Teléfono': parent.phone ? `${parent.country_code} ${parent.phone}` : '',
      'Estado': parent.is_active ? 'Activo' : 'Inactivo',
      'Estudiantes': parent.students_count || 0,
      'Total Pagado': parent.total_paid || 0,
      'Notas': parent.notes || ''
    }));
    
    exportToExcel(data, 'padres');
  };

  const getStatusChip = (isActive: boolean) => {
    return isActive ? (
      <Chip
        icon={<CheckCircleIcon />}
        label="Activo"
        color="success"
        size="small"
      />
    ) : (
      <Chip
        icon={<WarningIcon />}
        label="Inactivo"
        color="default"
        size="small"
      />
    );
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'name',
      headerName: 'Nombre',
      width: 250,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main' }}>
            <PersonIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.row.spanish_name || params.row.russian_name}
            </Typography>
            {params.row.spanish_name && params.row.russian_name && (
              <Typography variant="caption" color="text.secondary">
                {params.row.russian_name}
              </Typography>
            )}
          </Box>
        </Box>
      )
    },
    {
      field: 'contact',
      headerName: 'Contacto',
      width: 200,
      renderCell: (params) => (
        <Box>
          {params.row.email && (
            <Box display="flex" alignItems="center">
              <EmailIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="body2">{params.row.email}</Typography>
            </Box>
          )}
          {params.row.phone && (
            <Box display="flex" alignItems="center" sx={{ mt: 0.5 }}>
              <PhoneIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="body2">
                {params.row.country_code} {params.row.phone}
              </Typography>
            </Box>
          )}
        </Box>
      )
    },
    {
      field: 'students_count',
      headerName: 'Estudiantes',
      width: 100,
      align: 'center',
      renderCell: (params) => (
        <Chip
          icon={<SchoolIcon />}
          label={params.value || 0}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'total_paid',
      headerName: 'Total Pagado',
      width: 120,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium" color="primary">
          ₽{(params.value || 0).toLocaleString()}
        </Typography>
      )
    },
    {
      field: 'is_active',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver detalles">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                loadParentDetails(params.row.id);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/parents/${params.row.id}/edit`);
              }}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedParent(params.row);
                setDeleteDialogOpen(true);
              }}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Padres/Tutores
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ mr: 1 }}
          >
            Exportar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/parents/new')}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Nuevo Padre
          </Button>
        </Box>
      </Box>

      {/* Badge de padres pendientes */}
      {pendingParents.length > 0 && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => setActiveTab(1)}>
              Ver {pendingParents.length} pendiente{pendingParents.length !== 1 ? 's' : ''}
            </Button>
          }
        >
          <Box display="flex" alignItems="center">
            <Badge badgeContent={pendingParents.length} color="error">
              <PendingIcon />
            </Badge>
            <Typography sx={{ ml: 2 }}>
              Hay {pendingParents.length} padre{pendingParents.length !== 1 ? 'es' : ''} pendiente{pendingParents.length !== 1 ? 's' : ''} de aprobación
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Todos los Padres" />
          <Tab 
            label={
              <Badge badgeContent={pendingParents.length} color="error">
                Pendientes
              </Badge>
            } 
          />
        </Tabs>
      </Paper>

      {activeTab === 0 ? (
        <>
          {/* Filtros */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  placeholder="Buscar por nombre, email o teléfono..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={filters.active}
                    label="Estado"
                    onChange={(e) => setFilters({...filters, active: e.target.value})}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Activos</MenuItem>
                    <MenuItem value="false">Inactivos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Estudiantes</InputLabel>
                  <Select
                    value={filters.has_students}
                    label="Estudiantes"
                    onChange={(e) => setFilters({...filters, has_students: e.target.value})}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Con estudiantes</MenuItem>
                    <MenuItem value="false">Sin estudiantes</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    startIcon={<FilterListIcon />}
                    onClick={() => loadParents()}
                    fullWidth
                  >
                    Filtrar
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setFilters({ search: '', active: '', has_students: '' });
                    }}
                  >
                    <RefreshIcon />
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Tabla */}
          <Paper sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={parents}
              columns={columns}
              loading={loading}
              paginationMode="server"
              rowCount={total}
              pageSizeOptions={[10, 25, 50, 100]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              onRowClick={(params) => loadParentDetails(params.row.id)}
              disableRowSelectionOnClick
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            />
          </Paper>
        </>
      ) : (
        /* Panel de padres pendientes */
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Padres Pendientes de Aprobación
          </Typography>
          
          {loadingPending ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : pendingParents.length === 0 ? (
            <Alert severity="success">
              No hay padres pendientes de aprobación
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {pendingParents.map((parent) => (
                <Grid size={{ xs: 12 }} key={parent.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, md: 4 }}>
                          <Box display="flex" alignItems="center">
                            <Avatar sx={{ mr: 2, bgcolor: 'warning.main' }}>
                              <PersonIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1">
                                {parent.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {parent.email}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <Box display="flex" alignItems="center">
                            <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {parent.country_code} {parent.phone}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <Chip
                            icon={<PendingIcon />}
                            label="Pendiente de verificación"
                            color="warning"
                            size="small"
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }}>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() => handleApprove(parent.id)}
                            fullWidth
                          >
                            Aprobar
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}

      {/* Dialog de detalles */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">
                {parentDetails?.spanish_name || parentDetails?.russian_name}
              </Typography>
              {parentDetails?.spanish_name && parentDetails?.russian_name && (
                <Typography variant="caption" color="text.secondary">
                  {parentDetails.russian_name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {parentDetails && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Información de Contacto
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email"
                      secondary={parentDetails.email || 'No especificado'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <PhoneIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Teléfono"
                      secondary={parentDetails.phone ? 
                        `${parentDetails.country_code} ${parentDetails.phone}` : 
                        'No especificado'
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Estadísticas
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <SchoolIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Estudiantes a cargo"
                      secondary={parentDetails.students?.length || 0}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <PaymentsIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Total pagado"
                      secondary={`₽${(parentDetails.total_paid || 0).toLocaleString()}`}
                    />
                  </ListItem>
                </List>
              </Grid>

              {parentDetails.students && parentDetails.students.length > 0 && (
                <Grid size={{ xs: 12}}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Estudiantes a Cargo
                  </Typography>
                  <Grid container spacing={1}>
                    {parentDetails.students.map((student: any) => (
                      <Grid size={{ xs: 12, md: 4, sm: 6 }} key={student.id}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2">
                              {student.first_name} {student.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Grado: {student.grade}
                            </Typography>
                            <Chip
                              size="small"
                              label={student.status === 'active' ? 'Activo' : student.status}
                              color={student.status === 'active' ? 'success' : 'default'}
                              sx={{ mt: 1 }}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}

              {parentDetails.notes && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Notas
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2">
                      {parentDetails.notes}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Cerrar</Button>
          <Button
            variant="contained"
            onClick={() => {
              setDetailsDialogOpen(false);
              navigate(`/parents/${parentDetails?.id}/edit`);
            }}
          >
            Editar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Está seguro de eliminar al padre/tutor{' '}
            <strong>
              {selectedParent?.spanish_name || selectedParent?.russian_name}
            </strong>?
          </Typography>
          {selectedParent?.students_count && selectedParent.students_count > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Este padre tiene {selectedParent.students_count} estudiante(s) asignado(s).
              Se eliminará la relación pero los estudiantes permanecerán.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ListItem personalizado para el dialog
const ListItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
    {children}
  </Box>
);

const ListItemIcon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ minWidth: 40, color: 'text.secondary' }}>
    {children}
  </Box>
);

const ListItemText: React.FC<{ primary: string; secondary?: string }> = ({ primary, secondary }) => (
  <Box>
    <Typography variant="body2" color="text.secondary">
      {primary}
    </Typography>
    <Typography variant="body1">
      {secondary || 'No especificado'}
    </Typography>
  </Box>
);

export default ParentList;